<template>
    <div id="stats" class="landscape-stats">
        <div class="l-grid">
            <div v-for="team in teams" :key="`l-court-${team.side}`" class="l-court" :class="`l-court--${team.side}`">
                <div class="p-court-title">On court</div>
                <div
                    v-for="item in team.court"
                    :key="item.player.id"
                    class="p-player l-player"
                    @click="$emit('player-click', item.player, team.side)"
                >
                    <div class="p-player__jersey l-player__band" :style="{ backgroundColor: team.color, color: team.textColor }">
                        <span class="l-player__number">{{ item.jersey }}</span>
                        <span class="l-player__surname">{{ item.player.surname || item.player.name }}</span>
                        <v-icon v-if="item.mvp" small color="amber" class="l-player__mvp" title="MVP">mdi-crown</v-icon>
                    </div>
                    <!-- stats around the photo: points / rebounds on the left, assists / fouls on the right -->
                    <div class="l-player__body">
                        <div class="l-stat l-stat--tl l-stat--main">
                            <span class="l-stat__value">{{ item.points }}</span>
                            <span class="l-stat__label">pts</span>
                        </div>
                        <div class="l-stat l-stat--bl">
                            <span class="l-stat__value">{{ item.rebounds }}</span>
                            <span class="l-stat__label">reb</span>
                        </div>
                        <div class="p-player__photo l-player__photo">
                            <v-icon color="grey"> mdi-account </v-icon>
                        </div>
                        <div class="l-stat l-stat--tr">
                            <span class="l-stat__value">{{ item.assists }}</span>
                            <span class="l-stat__label">ast</span>
                        </div>
                        <div class="l-stat l-stat--br">
                            <span class="l-stat__value p-player__fouls" :class="item.foulClass">{{ item.fouls }}</span>
                            <span class="l-stat__label">fouls</span>
                        </div>
                    </div>
                </div>
                <div v-if="!team.court.length" class="p-court-empty">
                    No players on court
                </div>
            </div>

            <div v-for="team in teams" :key="`l-actions-${team.side}`" class="l-actions" :class="`l-actions--${team.side}`">
                <div class="l-actions__team" :style="{ backgroundColor: team.color, color: team.textColor }">
                    {{ team.name }}
                </div>
                <div class="sd-section-label">Made</div>
                <div class="sd-grid sd-grid--3">
                    <v-btn
                        v-for="points in shotCategories"
                        :key="`made-${team.side}-${points.category}`"
                        class="sd-stat-btn l-btn"
                        depressed
                        :dark="!team.light"
                        :light="team.light"
                        :color="team.color"
                        @click="$emit('shot', { side: team.side, made: 'made', category: points.category })"
                    >
                        <span class="sd-stat-btn__big">{{ points.value }}</span>PT
                    </v-btn>
                </div>
                <div class="sd-section-label">Missed</div>
                <div class="sd-grid sd-grid--3">
                    <v-btn
                        v-for="points in shotCategories"
                        :key="`missed-${team.side}-${points.category}`"
                        class="sd-stat-btn sd-stat-btn--missed l-btn"
                        depressed
                        :style="{ borderColor: team.color }"
                        @click="$emit('shot', { side: team.side, made: 'missed', category: points.category })"
                    >
                        <v-icon small color="red">mdi-close</v-icon><span class="sd-stat-btn__big">{{ points.value }}</span>PT
                    </v-btn>
                </div>
                <div class="sd-section-label">Other</div>
                <div class="sd-grid sd-grid--2">
                    <v-btn
                        v-for="action in actions"
                        :key="`other-${team.side}-${action.category}`"
                        class="sd-stat-btn sd-stat-btn--other l-btn l-btn--other"
                        depressed
                        :dark="!team.light"
                        :light="team.light"
                        :color="team.color"
                        @click="$emit('action', action.category, team.side)"
                    >
                        <v-icon left small>{{ action.icon }}</v-icon>{{ action.label }}
                    </v-btn>
                </div>
                <stats-bench-toggle
                    class="l-bench-toggle"
                    :team="team"
                    :faces="4"
                    :open="openBenchSide === team.side"
                    @toggle="$emit('toggle-bench', team.side)"
                />
            </div>

            <div class="l-center">
                <slot name="clock" />
                <stats-team-stats :rows="teamStatRows" :color1="teams[0].color" :color2="teams[1].color" />
                <slot name="feed" />
                <v-btn block large outlined color="red darken-1" class="p-end-match l-end-match" @click="$emit('end-match')">
                    <v-icon left>mdi-flag-checkered</v-icon>End match
                </v-btn>
            </div>
        </div>

        <!-- opens over the content below, across the whole width -->
        <v-expand-transition>
            <stats-bench-panel
                v-if="openTeam"
                overlay
                :team="openTeam"
                @close="$emit('toggle-bench', openTeam.side)"
                @player-click="$emit('player-click', $event, openTeam.side)"
            />
        </v-expand-transition>
    </div>
</template>

<script>
    import StatsBenchToggle from '@/components/stats/StatsBenchToggle'
    import StatsBenchPanel from '@/components/stats/StatsBenchPanel'
    import StatsTeamStats from '@/components/stats/StatsTeamStats'

    // Tablet layout of the live stats page: on-court players | team buttons | clock, team stats, feed | team buttons | players.
    // Purely presentational: the page passes the team views (see Stats.vue teamViews) and handles the emitted actions.
    export default {
        name: 'StatsLandscape',
        components: { StatsBenchToggle, StatsBenchPanel, StatsTeamStats },
        props: {
            teams: {
                type: Array,
                required: true
            },
            openBenchSide: {
                type: Number,
                default: null
            },
            teamStatRows: {
                type: Array,
                default: () => []
            }
        },
        data: () => ({
            shotCategories: [
                { value: 1, category: '1pt' },
                { value: 2, category: '2pts' },
                { value: 3, category: '3pts' }
            ],
            actions: [
                { label: 'Sub', category: 'sub', icon: 'mdi-swap-horizontal' },
                { label: 'Foul', category: 'foul', icon: 'mdi-whistle-outline' },
                { label: 'Def. reb', category: 'dreb', icon: 'mdi-shield-outline' },
                { label: 'Off. reb', category: 'oreb', icon: 'mdi-arrow-up-bold-outline' },
                { label: 'Assist', category: 'asst', icon: 'mdi-handshake-outline' },
                { label: 'Steal', category: 'stl', icon: 'mdi-hand-back-right-outline' },
                { label: 'Block', category: 'blk', icon: 'mdi-cancel' },
                { label: 'Turnover', category: 'to', icon: 'mdi-alert-circle-outline' }
            ]
        }),
        computed: {
            openTeam() {
                return this.teams.find(team => team.side === this.openBenchSide) || null
            }
        }
    }
</script>
